import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, MarkEmailRead as MarkEmailReadIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { BULLETIN_POLL_INTERVAL_MS, type BulletinPostsListResponse } from '../constants/bulletin';
import type { PatientAssignee } from '../constants/patient';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import BulletinFeedPostRow from './BulletinFeedPostRow';

const limit = 20;

type BulletinHomeCardProps = {
  /** 親が高さを与えるとき true（ホームのモバイルタブなど）。一覧エリアが内部スクロールになる */
  fillAvailableHeight?: boolean;
};

/**
 * ホーム左カラム: 掲示板一覧（未読フィルタ・ページネーション・一括既読）
 */
const BulletinHomeCard: React.FC<BulletinHomeCardProps> = ({ fillAvailableHeight = false }) => {
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const [data, setData] = useState<BulletinPostsListResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [readAllBusy, setReadAllBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  /** 空文字 = 投稿者で絞り込みなし */
  const [authorStaffId, setAuthorStaffId] = useState<number | ''>('');
  const [bodySearchInput, setBodySearchInput] = useState('');
  const [bodySearchDebounced, setBodySearchDebounced] = useState('');
  const [expiringSoon, setExpiringSoon] = useState(false);
  const [staffOptions, setStaffOptions] = useState<PatientAssignee[]>([]);
  const listEtagRef = useRef<string | null>(null);

  const listFiltersActive =
    authorStaffId !== '' || bodySearchDebounced.length > 0 || expiringSoon;

  const fetchList = useCallback(async (opts?: { silent?: boolean; conditional?: boolean }) => {
    const silent = opts?.silent ?? false;
    const conditional = opts?.conditional ?? false;
    const filtersActive =
      authorStaffId !== '' || bodySearchDebounced.length > 0 || expiringSoon;
    if (!silent) {
      setError('');
      setLoading(true);
    }
    try {
      const headers: Record<string, string> = {};
      if (conditional && silent && listEtagRef.current && !filtersActive) {
        headers['If-None-Match'] = listEtagRef.current;
      }
      const res = await apiClient.get<BulletinPostsListResponse>('/api/bulletin/posts', {
        params: {
          page,
          limit,
          ...(unreadOnly ? { unread_only: true } : {}),
          ...(authorStaffId !== '' ? { author_staff_id: authorStaffId } : {}),
          ...(bodySearchDebounced ? { body_q: bodySearchDebounced } : {}),
          ...(expiringSoon ? { expiring_soon: true } : {}),
        },
        headers,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
      });
      if (res.status === 304) {
        return;
      }
      setData(res.data);
      const etag = res.headers.etag;
      if (typeof etag === 'string') {
        listEtagRef.current = etag;
      }
    } catch {
      if (!silent) {
        setError('掲示板一覧の取得に失敗しました');
        setData(null);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [page, unreadOnly, authorStaffId, bodySearchDebounced, expiringSoon]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setBodySearchDebounced(bodySearchInput.trim());
    }, 300);
    return () => window.clearTimeout(id);
  }, [bodySearchInput]);

  useEffect(() => {
    setPage(1);
  }, [bodySearchDebounced]);

  useEffect(() => {
    let cancelled = false;
    void apiClient
      .get<PatientAssignee[]>('/api/staff-directory')
      .then((res) => {
        if (!cancelled) setStaffOptions(res.data);
      })
      .catch(() => {
        if (!cancelled) setStaffOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      void fetchList({ silent: true, conditional: true });
    }, BULLETIN_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [fetchList]);

  const handleReadAll = async () => {
    setReadAllBusy(true);
    try {
      await apiClient.post('/api/bulletin/read-all');
      showSnackbar({ message: 'すべて既読にしました', severity: 'success' });
      await fetchList();
    } catch {
      setError('一括既読の更新に失敗しました');
    } finally {
      setReadAllBusy(false);
    }
  };

  const unreadCount = data?.posts.filter((p) => p.is_unread).length ?? 0;

  const emptyMessage = (
    <Box sx={{ py: 5, px: 2, textAlign: 'center', color: 'text.secondary' }}>
      <Typography variant="body2">
        {loading
          ? '読み込み中…'
          : error
            ? '一覧を表示できませんでした'
            : listFiltersActive
              ? '該当する投稿はありません'
              : unreadOnly
                ? '未読の投稿はありません'
                : '投稿はまだありません'}
      </Typography>
    </Box>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        height: fillAvailableHeight ? '100%' : { xs: 'auto', md: '100%' },
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: fillAvailableHeight ? 'hidden' : { md: 'hidden' },
      }}
    >
      <CardHeader
        title="掲示板"
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
        sx={{ pb: 0, flexShrink: 0 }}
      />
      <CardContent
        sx={{
          pt: 1,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            flexShrink: 0,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="bulletin-filter-author">投稿者</InputLabel>
            <Select
              labelId="bulletin-filter-author"
              label="投稿者"
              value={authorStaffId === '' ? '' : String(authorStaffId)}
              onChange={(e) => {
                const v = e.target.value;
                setAuthorStaffId(v === '' ? '' : Number(v));
                setPage(1);
              }}
            >
              <MenuItem value="">
                <em>すべて</em>
              </MenuItem>
              {staffOptions.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="検索"
            placeholder=""
            value={bodySearchInput}
            onChange={(e) => setBodySearchInput(e.target.value)}
            sx={{ minWidth: 140, flex: '1 1 140px' }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 3,
            mb: 2,
            flexShrink: 0,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={unreadOnly}
                onChange={(_, checked) => {
                  setUnreadOnly(checked);
                  setPage(1);
                }}
                color="primary"
                size="small"
              />
            }
            label="未読のみ"
            sx={{ mr: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={expiringSoon}
                onChange={(_, c) => {
                  setExpiringSoon(c);
                  setPage(1);
                }}
              />
            }
            label="7日以内に期限"
            sx={{ mr: 0 }}
          />
          <Badge
            overlap="rectangular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            max={99}
            badgeContent={unreadCount}
            invisible={unreadCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                right: -4,
                top: 0,
                minWidth: 18,
                height: 18,
                px: 0.5,
                fontSize: '0.6875rem',
                fontWeight: 700,
                lineHeight: 1,
                borderRadius: 999,
                bgcolor: '#e53935',
                color: '#fff',
                border: '2px solid',
                borderColor: 'background.paper',
                boxSizing: 'content-box',
              },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<MarkEmailReadIcon />}
              onClick={() => void handleReadAll()}
              disabled={readAllBusy || !data?.posts.length}
            >
              すべて既読
            </Button>
          </Badge>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/bulletin/new')}
          >
            新規投稿
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: 0.5 }}>
          <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
            {!data || data.posts.length === 0
              ? emptyMessage
              : data.posts.map((p, i) => (
                  <React.Fragment key={p.id}>
                    {i > 0 && <Divider />}
                    <BulletinFeedPostRow post={p} onOpen={() => navigate(`/bulletin/${p.id}`)} avatarSize={32} />
                  </React.Fragment>
                ))}
          </Paper>
        </Box>

        {data && data.posts.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, flexShrink: 0 }}>
            <Pagination
              count={Math.max(1, data.total_pages)}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default BulletinHomeCard;
